import { setHidden, setTextContent } from './controllerDom.js';
import { state as controllerState } from './controllerState.js';
import { FACILITY_ROLES, FLOOD_LOCK_THRESHOLD } from '../shared/constants.js';
import type { Role, RoomState } from '../shared/types.js';
import { createSubmarineMapView, type SubmarineMapView } from './submarineMap/submarineMapView.js';

let activeMapView: 'submarine' | 'base' = 'submarine';

export const visibleControllerRoles: Role[] = [
  'engine',
  'battery',
  'engineer',
  'sonar',
  'periscope',
  'pilot',
  'ballast',
  'docking',
  'storage',
  'navigation',
  'comms',
  'captain',
  'life_support',
  'drone',
  'mining',
  'quarters',
  'weapons',
  'weapons_aft'
];

export const renderRoleButtons = (
  roomState: RoomState,
  lobbyRoleButtons: HTMLDivElement | null,
  controllerRoleTabs: HTMLDivElement | null,
  createRoleButton: (role: Role, roomState: RoomState, compact: boolean) => HTMLButtonElement
): void => {
  if (lobbyRoleButtons) {
    lobbyRoleButtons.innerHTML = '';
    for (const role of visibleControllerRoles) {
      lobbyRoleButtons.append(createRoleButton(role, roomState, false));
    }
  }

  if (controllerRoleTabs) {
    controllerRoleTabs.innerHTML = '';
    for (const role of visibleControllerRoles) {
      controllerRoleTabs.append(createRoleButton(role, roomState, true));
    }
  }
};

const setRoomMapStatus = (roomMapStatus: HTMLElement | null, message: string): void => {
  setTextContent(roomMapStatus, message);
};

// ---------------------------------------------------------------------------
// The room selector renders the shared submarine cutaway (selector density).
// The SVG views persist across renderRoomMap calls so player markers can
// animate; callbacks capture the latest handler/meta refs below.
// ---------------------------------------------------------------------------

let submarineView: SubmarineMapView | null = null;
let facilityView: SubmarineMapView | null = null;
let latestRoleMeta: ((role: Role) => { label: string; icon: string }) | null = null;
let latestHandleRoleTap: ((role: Role, roomState: RoomState) => void) | null = null;
let latestPanel: HTMLElement | null = null;
let latestStatus: HTMLElement | null = null;
/** True on the previous render if "we" were mid-walk — used to auto-close on arrival. */
let wasWalking = false;
/**
 * Self role as of the previous render while the panel has been open
 * (undefined = not yet observed since the panel opened). Entering a room —
 * by instant assignment or walk arrival — closes the panel.
 */
let selfRoleTracked: Role | null | undefined;
let walkStatusTimer: number | null = null;
let walkArriveAtLocal = 0;
let walkTargetLabel = '';

const findSelf = (roomState: RoomState) =>
  controllerState.currentPlayerId
    ? roomState.players.find((player) => player.id === controllerState.currentPlayerId) ?? null
    : null;

const stopWalkStatusTimer = (): void => {
  if (walkStatusTimer !== null) {
    window.clearInterval(walkStatusTimer);
    walkStatusTimer = null;
  }
};

const updateWalkStatus = (): void => {
  const remaining = Math.max(0, Math.ceil((walkArriveAtLocal - performance.now()) / 1000));
  setRoomMapStatus(latestStatus, `Walking to ${walkTargetLabel}… ${remaining}s`);
};

const handleRoomTap = (role: Role): void => {
  const roomState = controllerState.lastRoomState;
  if (!roomState) return;

  if (controllerState.currentRole === role) {
    setRoomMapStatus(latestStatus, 'Already in that room.');
    return;
  }

  // availableRoles does not exclude sealed rooms (the server rejects them at
  // assignRole), so check the flood lock first for the right message.
  const floodLevel = controllerState.lastSnapshot?.submarine.roomFlood?.[role] ?? 0;
  if (floodLevel >= FLOOD_LOCK_THRESHOLD) {
    setRoomMapStatus(latestStatus, 'Room flooded — sealed off.');
    return;
  }

  const available = roomState.availableRoles.includes(role);
  if (!available) {
    if (roomState.players.some((player) => player.transit?.to === role)) {
      setRoomMapStatus(latestStatus, 'Someone is already walking there.');
    } else {
      setRoomMapStatus(latestStatus, 'That room is occupied.');
    }
    return;
  }

  latestHandleRoleTap?.(role, roomState);

  if (FACILITY_ROLES.has(role)) {
    toggleRoomMapPanel(false, latestPanel, latestStatus);
    return;
  }
  // Submarine switches mid-game start a walk; the roomState broadcast that
  // follows drives the walking status + marker. Instant switches (lobby /
  // first pick) close via the roleAssigned → arrival path below.
  setRoomMapStatus(latestStatus, `Heading to ${latestRoleMeta?.(role).label ?? role}…`);
};

export const initMapViewForRole = (): void => {
  const docked = controllerState.lastSnapshot?.submarine.docked ?? false;
  if (docked && controllerState.currentRole && FACILITY_ROLES.has(controllerState.currentRole)) {
    activeMapView = 'base';
  } else if (!docked) {
    activeMapView = 'submarine';
  }
};

export const toggleRoomMapPanel = (
  open: boolean,
  roomMapPanel: HTMLElement | null,
  roomMapStatus: HTMLElement | null
): void => {
  if (!roomMapPanel) {
    return;
  }

  setHidden(roomMapPanel, !open);
  if (!open) {
    stopWalkStatusTimer();
    // Drop the role baseline so the next open re-observes it fresh. (Open
    // call sites render the map — taking the baseline — before toggling,
    // so the baseline must survive the open toggle.)
    selfRoleTracked = undefined;
  }
  setRoomMapStatus(roomMapStatus, 'Select a room.');
};

const ensureView = (layout: 'submarine' | 'facility'): SubmarineMapView => {
  if (layout === 'facility') {
    if (!facilityView) {
      facilityView = createSubmarineMapView({
        mode: 'selector',
        layout: 'facility',
        resolveRoleMeta: (role) => latestRoleMeta?.(role) ?? { label: role, icon: '' },
        onRoomTap: handleRoomTap,
        getSelfPlayerId: () => controllerState.currentPlayerId
      });
    }
    return facilityView;
  }
  if (!submarineView) {
    submarineView = createSubmarineMapView({
      mode: 'selector',
      layout: 'submarine',
      resolveRoleMeta: (role) => latestRoleMeta?.(role) ?? { label: role, icon: '' },
      onRoomTap: handleRoomTap,
      getSelfPlayerId: () => controllerState.currentPlayerId
    });
  }
  return submarineView;
};

export const renderRoomMap = (
  roomState: RoomState,
  roomMapGrid: HTMLDivElement | null,
  roomMapPanel: HTMLElement | null,
  roomMapStatus: HTMLElement | null,
  getRoleMeta: (role: Role) => { label: string; icon: string },
  handleRoleTap: (role: Role, roomState: RoomState) => void,
  roomMapTabs: HTMLDivElement | null = null
): void => {
  if (!roomMapGrid) {
    return;
  }

  latestRoleMeta = getRoleMeta;
  latestHandleRoleTap = handleRoleTap;
  latestPanel = roomMapPanel;
  latestStatus = roomMapStatus;

  const docked = controllerState.lastSnapshot?.submarine.docked ?? false;

  // If undocked, force back to submarine view
  if (!docked) {
    activeMapView = 'submarine';
  }

  // Render tab toggle above the grid when docked
  if (roomMapTabs) {
    roomMapTabs.innerHTML = '';
    roomMapTabs.hidden = !docked;

    if (docked) {
      for (const view of ['submarine', 'base'] as const) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `room-map-view-tab${activeMapView === view ? ' active' : ''}`;
        btn.textContent = view === 'submarine' ? 'SUBMARINE' : 'BASE';
        btn.addEventListener('click', () => {
          activeMapView = view;
          renderRoomMap(roomState, roomMapGrid, roomMapPanel, roomMapStatus, getRoleMeta, handleRoleTap, roomMapTabs);
        });
        roomMapTabs.append(btn);
      }
    }
  }

  const layout = activeMapView === 'base' && docked ? 'facility' : 'submarine';
  const view = ensureView(layout);
  const other = layout === 'facility' ? submarineView : facilityView;
  if (other && other.el.parentElement === roomMapGrid) {
    other.el.remove();
  }
  if (view.el.parentElement !== roomMapGrid) {
    roomMapGrid.setAttribute('data-layout', layout === 'facility' ? 'base' : 'submarine');
    roomMapGrid.append(view.el);
  }
  view.update(roomState, controllerState.lastSnapshot ?? null);

  // Walking state: status countdown while our own transit is active. The
  // panel closes once we're standing in a newly entered room — whether the
  // switch was instant (lobby / first pick) or a walk that just completed.
  const self = findSelf(roomState);
  const selfRole = self?.role ?? null;
  let enteredRoom = false;
  if (self) {
    // Skip tracking on roomStates that transiently omit us, so a re-appearing
    // summary isn't mistaken for entering a room.
    enteredRoom = selfRoleTracked !== undefined && selfRole !== null && selfRole !== selfRoleTracked;
    selfRoleTracked = selfRole;
  } else if (!controllerState.currentPlayerId && selfRoleTracked === undefined) {
    // First join: we have no player identity yet (currentPlayerId is set only
    // once roleAssigned fires), so findSelf can't locate us and the baseline
    // above never runs. We hold no role, so seed the null baseline now —
    // otherwise entering our very first room wouldn't be detected and the
    // selector would stay open. (The transient-omission guard is unaffected:
    // it only applies once currentPlayerId is known.)
    selfRoleTracked = null;
  }
  const transit = self?.transit ?? null;
  if (transit) {
    walkArriveAtLocal = performance.now() + transit.remainingMs;
    walkTargetLabel = getRoleMeta(transit.to).label;
    updateWalkStatus();
    if (walkStatusTimer === null) {
      walkStatusTimer = window.setInterval(updateWalkStatus, 500);
    }
    wasWalking = true;
  } else {
    stopWalkStatusTimer();
    const walkFailed = wasWalking && selfRole === null;
    wasWalking = false;
    if (enteredRoom) {
      // In the room now — flip to its console.
      toggleRoomMapPanel(false, roomMapPanel, roomMapStatus);
    } else if (walkFailed) {
      setRoomMapStatus(roomMapStatus, 'Select a room.');
    }
  }
};

/** Drop the persistent map views (e.g. on leaving the room). */
export const disposeRoomMapViews = (): void => {
  stopWalkStatusTimer();
  submarineView?.dispose();
  submarineView = null;
  facilityView?.dispose();
  facilityView = null;
  wasWalking = false;
  selfRoleTracked = undefined;
};
